import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import { Print, FileDownload } from '@mui/icons-material';

interface IdCardEmployee {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  employeeNumber?: string;
  email?: string;
  phoneNumber?: string;
  position?: { title: string };
  department?: { name: string };
  entity?: { name: string };
  employmentStatus?: string;
  startDate?: string;
  photoUrl?: string;
}

interface EmployeeIdCardProps {
  employee: IdCardEmployee;
  companyName?: string;
  brandColor?: string;
}

// Generate initials from name
function getInitials(emp: IdCardEmployee): string {
  return [emp.firstName?.[0], emp.lastName?.[0]].filter(Boolean).join('').toUpperCase();
}

// Generate consistent avatar color from string
function stringToColor(str: string): string {
  const colors = ['#1976d2', '#7b1fa2', '#388e3c', '#f57c00', '#d32f2f', '#0288d1', '#5d4037', '#00796b'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

// Simple inline QR code using a data URL from qrserver (graceful fallback)
const QrCode: React.FC<{ value: string; size?: number }> = ({ value, size = 72 }) => {
  const encoded = encodeURIComponent(value);
  return (
    <Box
      component="img"
      src={`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encoded}&bgcolor=ffffff&color=000000&margin=4`}
      alt="QR"
      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
        // Fallback: hide if offline
        (e.target as HTMLImageElement).style.display = 'none';
      }}
      sx={{ width: size, height: size, display: 'block', border: '1px solid #ddd', borderRadius: 0.5 }}
    />
  );
};

// ── ID Card Face component ────────────────────────────────────────────────────

const IdCardFace: React.FC<{ employee: IdCardEmployee; companyName: string; brandColor: string }> = ({
  employee,
  companyName,
  brandColor,
}) => {
  const initials   = getInitials(employee);
  const avatarColor = employee.photoUrl ? undefined : stringToColor(`${employee.firstName}${employee.lastName}`);
  const qrValue    = employee.employeeNumber || employee.email || employee.id;

  return (
    <Box
      className="id-card-face"
      sx={{
        width: 320,
        height: 200,
        bgcolor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        overflow: 'hidden',
        fontFamily: '"Inter", "Roboto", sans-serif',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top colour bar */}
      <Box sx={{ height: 8, bgcolor: brandColor, flexShrink: 0 }} />

      {/* Company header */}
      <Box sx={{ px: 2, pt: 1, pb: 0.5, bgcolor: brandColor, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 22, height: 22, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 900, lineHeight: 1 }}>
            {companyName[0]}
          </Typography>
        </Box>
        <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
          {companyName}
        </Typography>
      </Box>

      {/* Card body */}
      <Box sx={{ flex: 1, display: 'flex', px: 2, py: 1.5, gap: 2, alignItems: 'center' }}>
        {/* Photo / Avatar */}
        <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={employee.photoUrl}
            sx={{
              width: 64,
              height: 64,
              bgcolor: avatarColor,
              fontSize: 22,
              fontWeight: 700,
              border: `2px solid ${brandColor}`,
            }}
          >
            {!employee.photoUrl && initials}
          </Avatar>
          <QrCode value={qrValue} size={52} />
        </Box>

        {/* Info */}
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2, color: '#1a1a2e', mb: 0.25 }}>
            {employee.firstName} {employee.lastName}
          </Typography>
          {employee.nickname && (
            <Typography sx={{ fontSize: 10, color: '#888', fontStyle: 'italic', mb: 0.25 }}>
              "{employee.nickname}"
            </Typography>
          )}
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: brandColor, mb: 0.5, lineHeight: 1.2 }}>
            {employee.position?.title || '—'}
          </Typography>

          <Divider sx={{ mb: 0.5, borderColor: '#f0f0f0' }} />

          {employee.department && (
            <Typography sx={{ fontSize: 10, color: '#555', lineHeight: 1.4 }}>
              🏢 {employee.department.name}
            </Typography>
          )}
          {employee.email && (
            <Typography sx={{ fontSize: 10, color: '#555', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              ✉️ {employee.email}
            </Typography>
          )}
          {employee.phoneNumber && (
            <Typography sx={{ fontSize: 10, color: '#555', lineHeight: 1.4 }}>
              📱 {employee.phoneNumber}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2, py: 0.75, bgcolor: '#f8f8f8', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#aaa', letterSpacing: 1, textTransform: 'uppercase' }}>
          Employee ID
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#333', letterSpacing: 1.5, fontFamily: 'monospace' }}>
          {employee.employeeNumber || '—'}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Back of card ──────────────────────────────────────────────────────────────

const IdCardBack: React.FC<{ employee: IdCardEmployee; companyName: string; brandColor: string }> = ({
  employee,
  companyName,
  brandColor,
}) => (
  <Box
    className="id-card-face"
    sx={{
      width: 320,
      height: 200,
      bgcolor: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: 2,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {/* Top bar */}
    <Box sx={{ height: 8, bgcolor: brandColor, flexShrink: 0 }} />

    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, gap: 1 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: 1, textTransform: 'uppercase' }}>
        If found, please return to
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: brandColor, textAlign: 'center' }}>
        {companyName}
      </Typography>
      {employee.entity?.name && employee.entity.name !== companyName && (
        <Typography sx={{ fontSize: 11, color: '#555', textAlign: 'center' }}>
          {employee.entity.name}
        </Typography>
      )}
      <Divider sx={{ width: '60%', my: 0.5 }} />
      <Typography sx={{ fontSize: 9, color: '#aaa', textAlign: 'center', lineHeight: 1.5 }}>
        This card is company property. Misuse may result in disciplinary action.
        {employee.startDate && ` Valid from ${new Date(employee.startDate).getFullYear()}.`}
      </Typography>
    </Box>

    {/* Footer */}
    <Box sx={{ px: 2, py: 0.75, bgcolor: '#f8f8f8', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: 9, color: '#bbb', letterSpacing: 0.5 }}>
        {employee.firstName} {employee.lastName} · {employee.employeeNumber}
      </Typography>
    </Box>
  </Box>
);

// ── Main exported component ───────────────────────────────────────────────────

const EmployeeIdCard: React.FC<EmployeeIdCardProps> = ({
  employee,
  companyName = 'Humanity Protocol',
  brandColor = '#C08A2E',
}) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=700,height=600');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>ID Card — ${employee.firstName} ${employee.lastName}</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap" rel="stylesheet">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { background: #f5f5f5; display: flex; justify-content: center; align-items: flex-start; padding: 40px; font-family: "Inter", sans-serif; }
          .print-area { display: flex; flex-direction: column; gap: 24px; align-items: center; }
          @media print {
            body { background: white; padding: 20px; }
            @page { size: A4; margin: 1cm; }
          }
        </style>
      </head>
      <body>
        <div class="print-area">
          ${content.innerHTML}
        </div>
        <script>window.onload = () => { window.print(); window.close(); }<\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Box>
      {/* Print styles injected globally for this session */}
      <Box ref={printRef} sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={0.5}>Front</Typography>
          <IdCardFace employee={employee} companyName={companyName} brandColor={brandColor} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={0.5}>Back</Typography>
          <IdCardBack employee={employee} companyName={companyName} brandColor={brandColor} />
        </Box>
      </Box>

      <Box display="flex" gap={2} justifyContent="center">
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>
          Print ID Card
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          Standard CR80 card (85.6 × 53.98mm) — print on cardstock or send to a card printer
        </Typography>
      </Box>
    </Box>
  );
};

export default EmployeeIdCard;
